import {capitalize, isEmpty} from 'lodash';
import pluralize from 'pluralize';
import React, {FC, PropsWithChildren} from 'react';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {AsyncHandler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {CommaFormatter} from '../../../../../../srcCommon/formatters/commaFormatter';
import {isGlobalAdmin, isTeamAdmin} from '../../../../../helpers/models/accessHelpers';
import {isTeammateNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {LensModel} from '../../../../../models/api/lensModel';
import {getAccess} from '../../../../../store/data/dataSelectors';
import {
  deleteInvalidLensIngredientsAsyncAction,
  deleteLensAsyncAction,
} from '../../../../../store/data/lenses/saveLensActions';
import {hideWorkspaceLensAsyncAction} from '../../../../../store/data/workspace/workspaceActions';
import {TooltipWithButtons, TooltipWithButtonsProps} from '../../../../common/tooltips/tooltipWithButtons';
import {LensIngredientsTypesEnum, LensValidation} from '../helpers/useLensValidation';

/*
 * Props.
 */

export enum LensesErrorTypesEnum {
  PRIVATE = 'private',
  SHARED = 'shared',
}

export interface LensesWorkspaceErrorTooltipProps {
  lens: LensModel;
  lensValidation: LensValidation;
}

/*
 * Component.
 */

export const LensesWorkspaceErrorTooltip: FC<PropsWithChildren<LensesWorkspaceErrorTooltipProps>> = (
  props,
) => {
  const {lens, lensValidation} = props;
  const dispatch = useAppDispatch();
  const onDelete: AsyncHandler = async () => {
    await dispatch(deleteLensAsyncAction({lensId: lens.id, lensName: lens.name}));
  };
  const onUpdate = async () => {
    await dispatch(deleteInvalidLensIngredientsAsyncAction(lens, lensValidation));
  };
  const onHide = async () => {
    await dispatch(hideWorkspaceLensAsyncAction(lens.id));
  };
  const access = useAppSelector(getAccess);
  const lensType = isTeammateNamespace(lens.namespace)
    ? LensesErrorTypesEnum.PRIVATE
    : LensesErrorTypesEnum.SHARED;

  const isAdmin = isTeamAdmin(lens.namespace, access) || isGlobalAdmin(access);
  const teamViewHasInaccessibleInboxes =
    lensType === LensesErrorTypesEnum.SHARED && lensValidation.inaccessibleInboxes.length > 0;
  const generateTooltipForType = () => {
    const tooltipProps =
      !teamViewHasInaccessibleInboxes && (lensType === LensesErrorTypesEnum.PRIVATE || isAdmin)
        ? generateLensErrorTooltip(lensValidation, onDelete, onUpdate)
        : generateSharedLensErrorTooltip(lensValidation, onHide);
    return <TooltipWithButtons {...tooltipProps} />;
  };

  return generateTooltipForType();
};

/*
 * Helpers.
 */

function generateLensErrorTooltip(
  lensValidation: LensValidation,
  onDelete?: AsyncHandler,
  onUpdate?: AsyncHandler,
): TooltipWithButtonsProps {
  const tooltipProps: TooltipWithButtonsProps = {};
  const {
    inaccessibleInboxes,
    allInboxesDeleted,
    allInboxesInaccessible,
    deletedInboxes,
    deletedIngredients,
    deletedIngredientsTypeAndCount,
  } = lensValidation;
  tooltipProps.title = (
    <Trans id="private-lenses-workspace-error-tooltip-title">View is no longer valid</Trans>
  );
  if (allInboxesDeleted) {
    tooltipProps.buttons = onDelete
      ? [
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onDelete}>
            <Trans id="lenses-error-delete-view">Delete view</Trans>
          </Button>,
        ]
      : undefined;
    tooltipProps.tooltip = (
      <Trans id="lenses-error-all-inboxes-deleted">All inboxes in this view have been deleted.</Trans>
    );
  } else if (allInboxesInaccessible) {
    tooltipProps.buttons = onDelete
      ? [
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onDelete}>
            <Trans id="lenses-error-delete-view">Delete view</Trans>
          </Button>,
        ]
      : undefined;
    tooltipProps.tooltip = (
      <Trans id="lenses-error-all-inboxes-no-access">Lost access to all inboxes in this view.</Trans>
    );
  } else {
    tooltipProps.buttons =
      onDelete && onUpdate
        ? [
            <Button variant={ButtonVariantsEnum.DARK_MODE_LINK} onClick={onDelete}>
              <Trans id="lens-dialog-delete">Delete</Trans>
            </Button>,
            <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onUpdate}>
              <Trans id="lens-dialog-update">Update view</Trans>
            </Button>,
          ]
        : undefined;

    if (inaccessibleInboxes.length > 0) {
      const hasNullInaccessibleInboxNames =
        inaccessibleInboxes.some(({name}) => name === null) || deletedInboxes[0]?.name === null;
      const inaccessibleInboxNames = inaccessibleInboxes.map(({name}) => `"${name}"`);
      if (inaccessibleInboxes.length === 1 && deletedInboxes.length === 1 && hasNullInaccessibleInboxNames) {
        tooltipProps.tooltip = (
          <Trans id="lenses-error-inboxes-deleted-access-null">
            A team Inbox has been deleted so the team inbox is no longer accessible to you. Update the view to
            automatically remove the inbox if you want to continue using it.
          </Trans>
        );
      } else if (inaccessibleInboxes.length === 1 && deletedInboxes.length === 1) {
        tooltipProps.tooltip = (
          <Trans id="lenses-error-inboxes-deleted-access">
            Team Inbox &quot;{deletedInboxes[0].name}&quot; has been deleted and team inbox{' '}
            <CommaFormatter items={inaccessibleInboxNames} /> is no longer accessible to you. Update the view
            to automatically remove the inbox if you want to continue using it.
          </Trans>
        );
      } else if (hasNullInaccessibleInboxNames) {
        tooltipProps.tooltip = (
          <Trans id="lenses-error-inboxes-lost-access-null">
            Lost access to {pluralize('team inbox', inaccessibleInboxes.length)}. Update the view to
            automatically remove the {pluralize('inbox', inaccessibleInboxes.length)} if you want to continue
            using it.
          </Trans>
        );
      } else {
        tooltipProps.tooltip = (
          <Trans id="lenses-error-inboxes-lost-access">
            Lost access to {pluralize('team inbox', inaccessibleInboxes.length)}{' '}
            <CommaFormatter items={inaccessibleInboxNames} />. Update the view to automatically remove the{' '}
            {pluralize('inbox', inaccessibleInboxes.length)} if you want to continue using it.
          </Trans>
        );
      }
    } else if (!isEmpty(deletedIngredients)) {
      if (deletedIngredients.length === 1 && deletedIngredients[0].name === null) {
        const {type} = deletedIngredients[0];
        tooltipProps.tooltip = (
          <Trans id="lenses-error-ingredient-deleted-null">
            A {getLensIngredientTypeString(type, 1, false, false)} has been deleted. Update the view to
            automatically remove the {type} if you want to continue using it.
          </Trans>
        );
      } else if (deletedIngredients.length === 1) {
        const {type, name} = deletedIngredients[0];

        tooltipProps.tooltip = (
          <Trans id="lenses-error-ingredient-deleted">
            {getLensIngredientTypeString(type, 1, true, false)} &quot;{name}&quot; has been deleted. Update
            the view to automatically remove the {type} if you want to continue using it.
          </Trans>
        );
      } else {
        const typeAndCountArr = Object.entries(deletedIngredientsTypeAndCount);
        const deletedIngredientNames = typeAndCountArr.map(([type, count]) =>
          getLensIngredientTypeString(type, count, false, true),
        );
        const deletedIngredientsTypeNames = typeAndCountArr.map(([type, count]) =>
          getLensIngredientTypeString(type, count),
        );

        tooltipProps.tooltip = (
          <Trans id="lenses-error-ingredients-deleted">
            <CommaFormatter items={deletedIngredientNames} /> have been deleted. Update the view to
            automatically remove the <CommaFormatter items={deletedIngredientsTypeNames} /> if you want to
            continue using it.
          </Trans>
        );
      }
    }
  }

  return tooltipProps;
}

function generateSharedLensErrorTooltip(
  lensValidation: LensValidation,
  onHide: AsyncHandler,
): TooltipWithButtonsProps {
  const tooltipProps: TooltipWithButtonsProps = {};
  const {inaccessibleInboxes} = lensValidation;

  tooltipProps.title = <Trans id="shared-lenses-workspace-error-tooltip-title">Lost access to view</Trans>;
  if (inaccessibleInboxes.length > 0 || lensValidation.allInboxesDeleted) {
    tooltipProps.buttons = [
      <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onHide}>
        <Trans id="shared-lenses-workspace-error-remove-button">Remove view</Trans>
      </Button>,
    ];
    tooltipProps.tooltip = (
      <Trans id="lenses-error-all-inboxes-deleted">
        This view has been updated and you no longer have access. Remove this view from your sidebar.
      </Trans>
    );
  }

  return tooltipProps;
}

function getLensIngredientTypeString(
  type: LensIngredientsTypesEnum | string,
  count?: number,
  isFirst?: boolean,
  hasOtherTypesDeleted?: boolean,
) {
  const baseText = isFirst ? capitalize(type) : type;
  return pluralize(baseText, count, hasOtherTypesDeleted);
}
